import { useMemo, useRef, useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import type { ColumnDef } from '@tanstack/react-table'
import { KanbanSquare, List, RefreshCw, Ship } from 'lucide-react'
import { toast } from 'sonner'
import {
  Button,
  Chip,
  DataTable,
  EmptyState,
  MetricCard,
  MetricStrip,
  PageHeaderBand,
  SearchInput,
  Select,
  Toolbar,
  ToolbarLeft,
  ToolbarRight,
  ViewToggle,
} from '@/components/ui'
import {
  useCancelPacket,
  useGenerateFinals,
  useMarkApproved,
  useMarkRejected,
  useMarkSent,
  usePackets,
} from '../api/queries'
import type { Packet, Stage } from '../api/types'
import { BOARD_STAGES, STAGE_LABELS, STAGE_SHORT_LABELS, STAGE_VARIANT, transitionAllowed } from '../utils/stage-meta'
import { PacketsKanban } from '../components/PacketsKanban'

type ViewMode = 'table' | 'kanban'

const VIEW_OPTIONS = [
  { value: 'table' as const, label: 'Table', icon: List },
  { value: 'kanban' as const, label: 'Kanban', icon: KanbanSquare },
]

type StageFilter = 'all' | Stage

const STAGE_FILTER_OPTIONS: { value: StageFilter; label: string }[] = [
  { value: 'all', label: 'All stages' },
  ...BOARD_STAGES.map((s) => ({ value: s, label: STAGE_LABELS[s] })),
]

/**
 * List/kanban home page for the export-docs pipeline. Fetches the full
 * (unfiltered) packet list — there's no server-side stats endpoint, so
 * the KPI strip and stage filter are both computed client-side. Volume
 * here is expected to be low (one row per qualifying outbound shipment),
 * so this doesn't need the pagination sales-inbox has.
 */
export function PacketsListPage() {
  const navigate = useNavigate()
  const [view, setView] = useState<ViewMode>('kanban')
  const [stageFilter, setStageFilter] = useState<StageFilter>('all')
  const [searchTerm, setSearchTerm] = useState('')
  const searchInputRef = useRef<HTMLInputElement>(null)

  const list = usePackets()
  const markSent = useMarkSent()
  const markApproved = useMarkApproved()
  const markRejected = useMarkRejected()
  const cancelPacket = useCancelPacket()
  const generateFinals = useGenerateFinals()

  const openPacket = (id: number) =>
    navigate({ to: '/export-docs/$packetId', params: { packetId: String(id) } })

  const allItems = list.data?.packets ?? []

  const counts = useMemo(() => {
    const byStage: Partial<Record<Stage, number>> = {}
    for (const p of allItems) byStage[p.stage] = (byStage[p.stage] ?? 0) + 1
    const active = allItems.filter((p) => p.stage !== 'finals_ready' && p.stage !== 'cancelled').length
    const needsAttention = allItems.filter(
      (p) => p.stage === 'generation_failed' || p.stage === 'rejected',
    ).length
    const awaitingApproval = byStage.sent_to_intermediary ?? 0
    const readyForFinals = byStage.approved ?? 0
    return { active, needsAttention, awaitingApproval, readyForFinals }
  }, [allItems])

  const filtered = useMemo(() => {
    let rows = stageFilter === 'all' ? allItems : allItems.filter((p) => p.stage === stageFilter)
    const needle = searchTerm.trim().toLowerCase()
    if (needle) {
      rows = rows.filter((p) =>
        [p.picking_name, p.ordering_party_name, p.sale_order_name, p.lane_name]
          .filter(Boolean)
          .some((v) => v!.toLowerCase().includes(needle)),
      )
    }
    return rows
  }, [allItems, stageFilter, searchTerm])

  function transitionPacket(packet: Packet, target: Stage) {
    if (!transitionAllowed(packet.stage, target)) return
    const onError = (err: unknown) => {
      const msg = err instanceof Error ? err.message : 'transition failed'
      toast.error(msg, { duration: 6000 })
    }
    if (target === 'approved') {
      markApproved.mutate(packet.id, { onError })
    } else if (target === 'rejected') {
      markRejected.mutate({ packetId: packet.id, reason: undefined }, { onError })
    } else if (target === 'sent_to_intermediary') {
      markSent.mutate(packet.id, { onError })
    } else if (target === 'cancelled') {
      cancelPacket.mutate({ packetId: packet.id, reason: undefined }, { onError })
    } else if (target === 'finals_generating') {
      generateFinals.mutate(packet.id, { onError })
    } else {
      toast.error(
        `"${STAGE_LABELS[target]}" isn't reachable by drag — open the packet to use its action buttons.`,
      )
    }
  }

  const columns = useColumns()

  return (
    <div className="flex h-full min-h-0 w-full flex-col bg-canvas">
      <PageHeaderBand
        title="Export Documentation Packet"
        description="Draft, review, and finalize export document sets for Brazil shipments — Ordering Party vs. Sertrading intermediary kept visually separate at every step."
      >
        <div className="ml-auto">
          <MetricStrip variant="inline">
            <MetricCard variant="inline" label="Active" value={String(counts.active)} />
            <MetricCard variant="inline" label="Needs attention" value={String(counts.needsAttention)} />
            <MetricCard variant="inline" label="Awaiting approval" value={String(counts.awaitingApproval)} />
            <MetricCard variant="inline" label="Ready for finals" value={String(counts.readyForFinals)} />
          </MetricStrip>
        </div>
      </PageHeaderBand>

      <Toolbar>
        <SearchInput
          ref={searchInputRef}
          containerClassName="flex-1 max-w-none"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search shipment, ordering party, SO #, lane…"
        />
      </Toolbar>
      <Toolbar>
        <ToolbarLeft>
          <Select
            value={stageFilter}
            onChange={(e) => setStageFilter(e.target.value as StageFilter)}
            aria-label="Filter by stage"
          >
            {STAGE_FILTER_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </Select>
          <Button variant="secondary" size="sm" onClick={() => list.refetch()} disabled={list.isFetching}>
            <RefreshCw size={14} className="mr-1.5" />
            Refresh
          </Button>
        </ToolbarLeft>
        <ToolbarRight>
          <ViewToggle<ViewMode> options={VIEW_OPTIONS} value={view} onChange={setView} aria-label="View mode" />
        </ToolbarRight>
      </Toolbar>

      <div className="flex-1 min-h-0 overflow-y-auto px-6 py-4">
        {list.isLoading ? (
          <div className="flex flex-col items-center justify-center py-12">
            <p className="m-0 text-sm text-tertiary">Loading…</p>
          </div>
        ) : filtered.length === 0 ? (
          <EmptyState
            icon={Ship}
            title="No export packets here yet"
            description={
              stageFilter === 'all'
                ? 'A packet is created automatically when an outbound picking for a customer on an export lane reaches ready-to-ship.'
                : `No packets match the "${STAGE_FILTER_OPTIONS.find((o) => o.value === stageFilter)?.label ?? stageFilter}" filter.`
            }
          />
        ) : view === 'kanban' ? (
          <PacketsKanban
            items={filtered}
            onSelect={(packet) => openPacket(packet.id)}
            onTransition={transitionPacket}
          />
        ) : (
          <DataTable
            data={filtered}
            columns={columns}
            loading={list.isLoading}
            getRowId={(r) => String(r.id)}
            onRowClick={(r) => openPacket(r.id)}
            moduleId="export-docs"
            maxHeight="100%"
            columnWidths={{
              picking_name: 130,
              ordering_party_name: 220,
              lane_name: 180,
              stage: 150,
              flags: 100,
              stage_updated_at: 130,
            }}
            minTableWidth={1000}
            emptyState={
              <EmptyState variant="no-match" title="No matches" description="Try clearing the stage filter or your search." />
            }
          />
        )}
      </div>
    </div>
  )
}

function useColumns(): ColumnDef<Packet, unknown>[] {
  return useMemo<ColumnDef<Packet, unknown>[]>(
    () => [
      {
        id: 'picking_name',
        header: 'Shipment',
        accessorFn: (r) => r.picking_name ?? '',
        cell: (ctx) => ctx.row.original.picking_name ?? <em className="text-tertiary">unnamed</em>,
      },
      {
        id: 'ordering_party_name',
        header: 'Ordering Party',
        accessorFn: (r) => r.ordering_party_name ?? '',
        cell: (ctx) => ctx.row.original.ordering_party_name ?? <em className="text-tertiary">unknown</em>,
      },
      {
        id: 'lane_name',
        header: 'Lane',
        accessorFn: (r) => r.lane_name ?? '',
        cell: (ctx) => <span className="text-secondary">{ctx.row.original.lane_name ?? '—'}</span>,
      },
      {
        id: 'stage',
        header: 'Stage',
        accessorFn: (r) => STAGE_SHORT_LABELS[r.stage],
        cell: (ctx) => (
          <Chip size="xs" variant={STAGE_VARIANT[ctx.row.original.stage]} dot>
            {STAGE_SHORT_LABELS[ctx.row.original.stage]}
          </Chip>
        ),
      },
      {
        id: 'flags',
        header: 'Flags',
        enableSorting: false,
        cell: (ctx) => {
          const { is_hazardous, coo_required } = ctx.row.original
          if (!is_hazardous && !coo_required) return <span className="text-secondary">—</span>
          return (
            <div className="flex items-center gap-1">
              {is_hazardous ? (
                <Chip size="xs" variant="warning">
                  Hazmat
                </Chip>
              ) : null}
              {coo_required ? (
                <Chip size="xs" variant="info">
                  CoO
                </Chip>
              ) : null}
            </div>
          )
        },
      },
      {
        id: 'stage_updated_at',
        header: 'Updated',
        accessorKey: 'stage_updated_at',
        cell: (ctx) => <span className="text-tertiary">{formatDate(ctx.row.original.stage_updated_at)}</span>,
      },
    ],
    [],
  )
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
}
